import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty } from 'underscore';
import { withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { useFormService } from '@bingads-webui-react/form';
import {
  apiErrorMapper,
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import {
  FormErrorGroup, UnmappedFormErrors as OriginalUnmappedFormErrors,
} from '@bingads-webui-campaign-react/form-error';
import { StepHeader } from '@bingads-webui-campaign-react/pmax-common';
import {
  Collapse,
  useCollapseState,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { LanguageTargetPanel, AudienceSignalSection, PANEL_KEYS } from '@bingads-webui-campaign-react/wizard-shared-components';
import { LocationTargetingSettingPanel, AudienceSignalPanel, useIsMounted } from '@bingads-webui-campaign-react/step-wizard-controls';
import { CampaignValidationGroup, AssetGroupValidationGroup, SystemErrors } from '@bingads-webui-campaign-react/unified-app-shared';
import { PlacementTargetPanel, PANEL_KEYS as PANEL_KEYS_V2 } from '@bingads-webui-campaign-react/wizard-shared-components-v2';

import { useTargetingStepStyles } from './use-targeting-step-styles';

const UnmappedFormErrors = withControlErrorHandling(OriginalUnmappedFormErrors);

export const TargetingStepShell = ({
  data: targetingInfo = {},
  onDataChange,
  error,
}) => {
  const isLoading = useMemo(() => isEmpty(targetingInfo), [targetingInfo]);
  const isMounted = useIsMounted();

  const getStyles = useTargetingStepStyles();

  const { serverErrors, systemErrors } = error || {};

  const { state: { context: { saveFailed } } } = useFormService();
  const i18n = useContext(I18nContext);
  const perfMarker = useContext(PerformanceMarkerContext);

  const {
    bundleCountries,
    bundleLocationCriterions,
    campaign = {},
    assetGroup = {},
  } = targetingInfo || {};

  const [
    allSections,
    expandedSections,
    setExpandedSections,
  ] = useCollapseState(
    // PANEL_KEYS_V2.PLACEMENT_TARGET should be added to these arrays once it's ready to enable it
    [PANEL_KEYS.LOCATIONS, PANEL_KEYS.LANGUAGE_TARGET, PANEL_KEYS.AUDIENCE_SIGNAL],
    [PANEL_KEYS.LOCATIONS, PANEL_KEYS.LANGUAGE_TARGET, PANEL_KEYS.AUDIENCE_SIGNAL]
  );

  const targetingExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setExpandedSections }),
    [setExpandedSections]
  );

  return (
    <ActuationGroup actuationProps={{ isActive: saveFailed }}>
      <AttentionBeaconGroup>
        <FormErrorGroup formErrorProps={{ errors: serverErrors, saveFailed, errorMapper: apiErrorMapper }}>
          <div className={classNames(getStyles('container'), 'targeting-step-container')}>
            <StepHeader
              title={_TL_('Who do you want to see your ads?')}
              description={_TL_('Choose where you want your ads to appear.')}
            />
            <UnmappedFormErrors className="other-server-errors" />
            <SystemErrors errors={systemErrors} />
            <CampaignValidationGroup
              data={campaign}
              onDataChange={onDataChange}
              isValidating={isMounted.current}
              i18n={i18n}
            >
              <Collapse
                allPanelItems={allSections}
                checkItems
                skipItemsTypeCheck
                activeKeys={expandedSections}
                setActiveKeys={setExpandedSections}
              >
                <LocationTargetingSettingPanel
                  key={PANEL_KEYS.LOCATIONS}
                  isLoading={isLoading}
                  options={{
                    bundleCountries,
                    bundleLocationCriterions,
                  }}
                  expand={targetingExpand}
                  data={campaign}
                  topicKey="pop_BA_Apps_Location"
                />
                <LanguageTargetPanel
                  key={PANEL_KEYS.LANGUAGE_TARGET}
                  isLoading={isLoading}
                  expand={targetingExpand}
                  data={campaign}
                  topicKey="pop_BA_Apps_Language"
                />
                <PlacementTargetPanel
                  key={PANEL_KEYS_V2.PLACEMENT_TARGET}
                  isLoading={isLoading}
                  expand={targetingExpand}
                  data={campaign}
                />
              </Collapse>
            </CampaignValidationGroup>
            <AssetGroupValidationGroup
              data={assetGroup}
              onDataChange={onDataChange}
              isValidating={isMounted.current}
              i18n={i18n}
            >
              <AudienceSignalSection>
                <Collapse
                  allPanelItems={allSections}
                  checkItems
                  skipItemsTypeCheck
                  activeKeys={expandedSections}
                  setActiveKeys={setExpandedSections}
                >
                  <AudienceSignalPanel
                    key={PANEL_KEYS.AUDIENCE_SIGNAL}
                    isLoading={isLoading}
                    options={{
                      wizardPerfMarker: perfMarker,
                    }}
                    expand={targetingExpand}
                    data={assetGroup}
                  />
                </Collapse>
              </AudienceSignalSection>
            </AssetGroupValidationGroup>
          </div>
        </FormErrorGroup>
      </AttentionBeaconGroup>
    </ActuationGroup>
  );
};

TargetingStepShell.propTypes = {
  data: PropTypes.shape({
    locationTargets: PropTypes.shape({}),
    bundleCountries: PropTypes.arrayOf(PropTypes.shape({})),
    bundleLocationCriterions: PropTypes.arrayOf(PropTypes.shape({})),
    campaign: PropTypes.shape({}),
    assetGroup: PropTypes.shape({}),
  }),
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  onDataChange: PropTypes.func.isRequired,
};

TargetingStepShell.defaultProps = {
  data: {},
  error: {},
};
